import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import type { OrderDetail } from '../types';

interface Props {
  orderId: number | null;
  open: boolean;
  onClose: () => void;
}

const fmtMoney = (n?: number | null) => {
  if (n == null) return '—';
  return `$ ${Math.round(n).toLocaleString('es-AR')}`;
};

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const iso = d.slice(0, 10);
  const [y, m, day] = iso.split('-');
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

const CHANNEL_COLORS: Record<string, string> = {
  'MercadoLibre': '#2D3277',
  'TiendaNube': '#1E88E5',
  'WhatsApp': '#25D366',
  'Manual': '#607D8B',
};
const channelLabel = (channelName?: string | null, reference?: string | null): string => {
  const ch = (channelName || '').toLowerCase();
  const ref = (reference || '').toUpperCase();
  if (ch.includes('mercado') || ch === 'ml' || ref.startsWith('ML-')) return 'MercadoLibre';
  if (ch.includes('tienda') || ch.includes('nube') || ref.startsWith('TN-')) return 'TiendaNube';
  if (ch.includes('whats') || ch.includes('wpp')) return 'WhatsApp';
  if (channelName && channelName.trim()) return channelName.trim();
  return 'Manual';
};

// Vista de solo lectura del detalle de una venta, usada como drilldown
// desde Flujo de Caja. La edición completa sigue viviendo en /sales.
export const OrderDetailDialog: React.FC<Props> = ({ orderId, open, onClose }) => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true);
    setError(null);
    setOrder(null);
    api.getOrder(orderId)
      .then(setOrder)
      .catch((e: any) => setError(e?.response?.data?.message || 'No se pudo cargar la venta'))
      .finally(() => setLoading(false));
  }, [open, orderId]);

  const balance = order ? (order.total || 0) - (order.paidAmount || 0) : 0;
  const cobrada = order ? (order.paymentStatus === 'Pagado' || balance <= 0.01) : false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Detalle de venta {order ? `· ${order.reference || ''}` : ''}
      </DialogTitle>
      <DialogContent>
        {loading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {order && (
          <Box>
            <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary">Cliente</Typography>
                <Typography variant="body1">{order.customerName || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Canal</Typography>
                <Box>
                  {(() => {
                    const canal = channelLabel(order.channelName, order.reference);
                    return <Chip size="small" label={canal} sx={{ bgcolor: CHANNEL_COLORS[canal] ?? '#607D8B', color: '#fff' }} />;
                  })()}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total</Typography>
                <Typography variant="body1" fontWeight={600}>{fmtMoney(order.total)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Cobrado</Typography>
                <Typography variant="body1" color="success.main">{fmtMoney(order.paidAmount)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Saldo</Typography>
                <Typography variant="body1" color={balance > 0.01 ? 'warning.main' : 'text.disabled'}>{fmtMoney(balance)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Estado de pago</Typography>
                <Box>
                  <Chip
                    size="small"
                    label={order.paymentStatus || 'Pendiente'}
                    color={cobrada ? 'success' : (balance > 0.01 ? 'warning' : 'default')}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Fecha de Cierre</Typography>
                <Typography variant="body1">{fmtDate(order.closeDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Vencimiento</Typography>
                <Typography variant="body1">{fmtDate(order.paymentDueDate)}</Typography>
              </Box>
            </Stack>

            {order.paymentInstallments && order.paymentInstallments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Cronograma de cuotas</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell align="right">%</TableCell>
                      <TableCell align="right">Monto</TableCell>
                      <TableCell>Método</TableCell>
                      <TableCell align="right">Días diferimiento</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.paymentInstallments.map(it => {
                      const amount = Math.round((order.total || 0) * (it.percentage || 0) / 100);
                      return (
                        <TableRow key={it.sequence}>
                          <TableCell>{it.sequence}</TableCell>
                          <TableCell align="right">{it.percentage}%</TableCell>
                          <TableCell align="right">{fmtMoney(amount)}</TableCell>
                          <TableCell>{it.paymentMethod}</TableCell>
                          <TableCell align="right">{it.defermentDays}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {order.notes && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Notas</Typography>
                <Typography variant="body2">{order.notes}</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {order && (
          <Button variant="contained" onClick={() => { onClose(); navigate(`/sales?openOrderId=${order.id}`); }}>
            Abrir en Ventas
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailDialog;
